/**
 * `ImportedModelRequestRegistry`
 *
 * 宿主层面（Editor / Tab 管理 / prepare-insertion）的请求归属 + artifact in-flight
 * 注册表。承担两项彼此独立的职责：
 *
 *   (1) **Ownership（请求归属 + 协作式抢占）**：同一 `(kind, scope)` bucket 下只允许
 *       一个 current handle。新 `acquire()` 到来时旧 handle 的 `isCurrent()` 同步翻
 *       false；是否同步 abort 旧 handle 的 `transaction` 由 `cooperative` 选项决定——
 *       非协作模式（默认）同步 abort 旧 transaction，用于 "tab 切换即取消" 场景；
 *       协作模式仅转交前台归属，让旧 loader 跑完并允许 caller 之后把解析结果 commit
 *       到共享 cache，避免用户快速来回切换 tab 时丢失已完成的下载/解析。loader 中断
 *       以 `transaction.isAborted` 为唯一信号。
 *
 *   (2) **Artifact in-flight dedup**：按 `artifactKey` 提供 `Promise<T>` 级别的在途
 *       复用（典型 key 为 `parsed-glb:${uuid}:${version}`）。artifact 的生命周期绑定
 *       到它自己的 promise——`registerInFlightArtifact` 在 promise settle（`.then` /
 *       `.catch`）时自动解注册；单个 handle abort 不会取消可能正被其他 handle 复用的
 *       在途 artifact。这套机制与 ownership 完全解耦。
 *
 * 当前已接入的 artifact 复用点：
 *   - `imported-model-loader.ts` 的顶层 GLB 下载+解析阶段；
 *   - `assembly-loader.ts` 的同层/嵌套 GLB 下载+解析阶段。
 *
 * 两处使用同一 `parsed-glb:${uuid}:${version}` 格式的 artifactKey 共享 in-flight
 * promise。该格式的权威定义集中在
 * `imported-model-loader.ts:buildParsedGlbArtifactKey`（经
 * `imported-model-artifact-key.ts` 提供实际实现）——任何 key 格式变动必须两边同步
 * 更新，否则跨 loader 的并发下载将无法命中同一 promise，dedup 失效。
 */

import {
  AssemblyModelCache,
  ParsedGlbCache,
  PartAssetCache,
} from '@puppycad/cad-core'

import { type ImportedModelPublishScopeKey } from './imported-model-load-scope'
import { ImportedModelLoadTransaction } from './imported-model-load-transaction'

/**
 * request 类别。
 * - `'preload'`：背景预取（tab 切换前的预热或默认 tab 预加载）。
 * - `'tab-switch'`：用户触发的前台 tab 切换加载。
 * - `'prepare-insertion'`：InsertCAD 的 staging 加载（Task 3 的 `prepareInsertion`）。
 */
export type ImportedModelRequestKind = 'preload' | 'tab-switch' | 'prepare-insertion'

export interface ImportedModelRequestAcquireOptions {
  /**
   * ownership 分桶维度。例如 `'tab-A'`、`'editor'`。同一 (kind, scope) 下新 handle 抢占
   * 旧 handle；不同 scope 之间互不影响。
   */
  scope: string
  /** 该 handle 上事务 commit 时写入的 cache scope，默认 project。 */
  publishScopeKey?: ImportedModelPublishScopeKey
  /**
   * 协作式抢占。当 `true`：被抢占的旧 handle 只丢失 `isCurrent()`（前台归属转交给新
   * handle），但其 `transaction` **不会被 registry 自动 abort** —— loader 继续跑完，
   * 允许 caller 在 `await` 返回后将已经解析好的 artifact commit 到共享 cache
   * （`partAssetCache` / `assemblyModelCache`）。这样用户快速往返 tab 时，旧 tab 的
   * 下载/解析成果不会丢，返回时可以直接缓存命中。
   *
   * 当 `false` 或未提供（默认）：维持旧语义——抢占时**同步 abort** 旧 handle 的
   * transaction（rollback staged assets）。用于 insertion 这类 "tab 切换即取消" 的场景。
   */
  cooperative?: boolean
}

export interface ImportedModelRequestHandle {
  /** 该 handle 的唯一 id；由 registry 生成，仅表示身份，不是 loader 中断信号来源。 */
  readonly requestId: string
  readonly kind: ImportedModelRequestKind
  readonly scope: string
  /** 与 handle 绑定的 loader 事务，loader 中断信号唯一来源 = transaction.isAborted。 */
  readonly transaction: ImportedModelLoadTransaction
  /** 是否当前仍是对应 (kind, scope) 的 ownership 持有者。 */
  isCurrent(): boolean
  /**
   * 放弃 ownership（不改动 transaction 状态）。commit / abort 由 caller 自行决定。
   * 典型：loader 已成功 commit → handle.release()。
   */
  release(): void
  /** 主动 abort：放弃 ownership 并 abort 绑定的 transaction（触发 rollback）。 */
  abort(): void
}

export interface ImportedModelRequestRegistryOptions {
  partAssetCache: PartAssetCache
  assemblyModelCache: AssemblyModelCache
  /**
   * 与 registry 内部 acquire 的 transaction 绑定的 parsedGlbCache。必须与宿主
   * （Editor / Tab）共享的 parsedGlbCache 是同一实例——transaction 在终态 evict
   * tracked parsedGlbCache 条目（Fix C）就是基于此句柄。
   */
  parsedGlbCache: ParsedGlbCache
}

interface InternalHandleState {
  handle: ImportedModelRequestHandle | undefined
  isCurrent: boolean
}

function makeBucketKey(kind: ImportedModelRequestKind, scope: string): string {
  return `${kind}::${scope}`
}

function generateRequestId(kind: ImportedModelRequestKind): string {
  // 不依赖 crypto；registry 是 in-memory、单进程的，足够唯一即可。
  const rand = Math.random().toString(36).slice(2, 10)
  return `${kind}-${Date.now().toString(36)}-${rand}`
}

export class ImportedModelRequestRegistry {
  private readonly partAssetCache: PartAssetCache
  private readonly assemblyModelCache: AssemblyModelCache
  private readonly parsedGlbCache: ParsedGlbCache

  /** 每个 (kind, scope) bucket 的当前 handle 记录。 */
  private readonly currentHandlesByBucket = new Map<string, InternalHandleState>()

  /** artifactKey → in-flight promise 的注册表。 */
  private readonly inFlightArtifacts = new Map<string, Promise<unknown>>()

  constructor(options: ImportedModelRequestRegistryOptions) {
    this.partAssetCache = options.partAssetCache
    this.assemblyModelCache = options.assemblyModelCache
    this.parsedGlbCache = options.parsedGlbCache
  }

  /**
   * acquire 一个新 handle，并抢占同 (kind, scope) 下的旧 handle（若存在）。
   *
   * **同步抢占语义（关键不变量）：**
   *   - 旧 handle 的 `isCurrent()` 在本方法**同步返回之前**就已翻 false；
   *   - 若 `options.cooperative !== true`（默认），旧 handle 绑定的 `transaction.abort()`
   *     也在本方法同步返回之前完成（包含 rollback & dispose 调用）；caller 在 await
   *     返回后通过 `oldHandle.isCurrent()`/`oldHandle.transaction.isAborted` 都能看到
   *     抢占已生效——中间不需要 await/microtask/next tick；
   *   - 若 `options.cooperative === true`，只做前台归属转交——旧 handle 的
   *     `isCurrent()` 翻 false，但 **不调用** `transaction.abort()`：旧 loader 继续跑，
   *     caller 可在 await 返回后 `commit()` 到共享 cache，使 tab 来回切换时旧下载/
   *     解析工作不会丢失。
   *
   * 这条不变量被 `useEditorGlbLoader.ts` 中 "preload ↔ tab-switch" / "重复 prepareInsertion"
   * 两条入口依赖：旧请求 await 回到宿主时，只要同 (kind, scope) 上有更新的 acquire，
   * 旧 handle 必然已经非 current（且在非协作模式下 isAborted），不会错发 publish
   * 或 hideLoading。
   */
  acquire(
    kind: ImportedModelRequestKind,
    options: ImportedModelRequestAcquireOptions,
  ): ImportedModelRequestHandle {
    const bucketKey = makeBucketKey(kind, options.scope)
    const previous = this.currentHandlesByBucket.get(bucketKey)
    if (previous && previous.handle) {
      // 始终把旧 handle 的前台归属 (isCurrent) 翻 false —— 新 handle 接管。
      previous.isCurrent = false
      // 仅当非协作模式时才 abort 旧 transaction；协作模式让旧 loader 跑完，caller
      // 负责在 await 回来后 commit 到共享 cache。
      if (!options.cooperative) {
        previous.handle.transaction.abort()
      }
    }

    const transaction = new ImportedModelLoadTransaction({
      partAssetCache: this.partAssetCache,
      assemblyModelCache: this.assemblyModelCache,
      parsedGlbCache: this.parsedGlbCache,
      // Transaction 构造方法已经处理默认 scope 回退，这里直接透传即可。
      publishScopeKey: options.publishScopeKey,
    })

    const requestId = generateRequestId(kind)
    const state: InternalHandleState = { handle: undefined, isCurrent: true }
    const handle: ImportedModelRequestHandle = {
      requestId,
      kind,
      scope: options.scope,
      transaction,
      isCurrent: () => state.isCurrent,
      release: () => {
        this.detachIfCurrent(bucketKey, handle, state)
      },
      abort: () => {
        // 抢占自身 + abort transaction（identity-guard 与 release 完全一致）
        this.detachIfCurrent(bucketKey, handle, state)
        transaction.abort()
      },
    }
    state.handle = handle

    this.currentHandlesByBucket.set(bucketKey, state)
    return handle
  }

  /**
   * 把给定 (bucketKey, handle, state) 从 `currentHandlesByBucket` 分离：
   *   - state.isCurrent 翻 false（幂等）；
   *   - 当且仅当当前 bucket 记录的 handle 身份 === 传入的 handle（identity guard）
   *     时，才删除 bucket 条目，避免把后继 handle 的条目误删。
   * release() / abort() 共用该方法以保证语义一致。
   */
  private detachIfCurrent(
    bucketKey: string,
    handle: ImportedModelRequestHandle,
    state: InternalHandleState,
  ): void {
    if (!state.isCurrent) {
      return
    }
    state.isCurrent = false
    if (this.currentHandlesByBucket.get(bucketKey)?.handle === handle) {
      this.currentHandlesByBucket.delete(bucketKey)
    }
  }

  /**
   * 注册一个 in-flight artifact promise。若 `artifactKey` 已有在途 promise，返回既有
   * 的那个（**不覆盖**），用于跨 handle 的 artifact 复用。
   *
   * 注册的 promise resolve 或 reject 后，registry 会自动解注册（无需 caller 手动 remove）。
   *
   * 当前消费者：当 caller 通过 loader options 传入本 registry 时，
   * `imported-model-loader.ts` 与 `assembly-loader.ts` 都以
   * `parsed-glb:${uuid}:${version}` 作为 key 调用本方法，把一次正在进行的 GLB 下载+解析
   * 共享给并发的抢占请求。key 格式权威定义见
   * `imported-model-loader.ts:buildParsedGlbArtifactKey`。
   */
  registerInFlightArtifact<T>(artifactKey: string, promise: Promise<T>): Promise<T> {
    const existing = this.inFlightArtifacts.get(artifactKey)
    if (existing) {
      return existing as Promise<T>
    }
    this.inFlightArtifacts.set(artifactKey, promise)
    // 解注册：resolve 或 reject 都清理（catch 防止 UnhandledPromiseRejection 级联）
    promise
      .then(
        () => {
          if (this.inFlightArtifacts.get(artifactKey) === promise) {
            this.inFlightArtifacts.delete(artifactKey)
          }
        },
        () => {
          if (this.inFlightArtifacts.get(artifactKey) === promise) {
            this.inFlightArtifacts.delete(artifactKey)
          }
        },
      )
      // 说明：上面两个 handler 都只 return undefined，所以这里的 `.catch` 不会从 handler
      // 的正常 return 值上捕获任何东西——它唯一能捕获的是 handler 自身在执行时抛出的
      // 同步异常。保留它是一道防御：如果将来编辑 handler 时不小心引入了会抛的代码，
      // 这个 catch 保证不会泄漏成 UnhandledPromiseRejection。
      .catch(() => {
        /* noop */
      })
    return promise
  }

  /**
   * 查询 artifactKey 对应的在途 promise；不存在返回 undefined。
   *
   * 当前消费者与 `registerInFlightArtifact` 一致：`imported-model-loader.ts` 与
   * `assembly-loader.ts` 在启动下载前先 `getInFlightArtifact`，命中即复用现有 promise；
   * 未命中才 `registerInFlightArtifact` 新的下载+解析任务。
   */
  getInFlightArtifact<T = unknown>(artifactKey: string): Promise<T> | undefined {
    return this.inFlightArtifacts.get(artifactKey) as Promise<T> | undefined
  }

  /**
   * 显式 abort 所有仍在 `currentHandlesByBucket` 中的 current handle，并清空 bucket。
   *
   * 语义：遍历每个 bucket 的 current handle，调用 `handle.abort()`——这一步会同时
   *   (a) 同步翻 `isCurrent` 为 false，
   *   (b) 同步 abort 绑定的 `transaction`（触发 rollback），
   *   (c) 通过 identity-guard 删除 bucket 条目；
   * 单条 `handle.abort()` 已经会从 map 里自摘除，遍历结束时 map 自然为空。
   *
   * 使用场景：宿主 `dispose()` 终态兜底——对没有被 caller 显式 abort 的遗留
   * preload / tab-switch handle 保证 transaction 被 rollback，不让 staged 资产
   * 撞到紧随其后的 `disposeCaches()`。additive API，不改变任何现有路径行为。
   */
  abortAllCurrent(): void {
    // 复制 entries 为数组——`handle.abort()` 会改动 `currentHandlesByBucket`，
    // 边遍历边删 Map 在 V8 里是安全的，但复制更清晰且对未来修改更稳。
    const snapshot = Array.from(this.currentHandlesByBucket.values())
    for (const state of snapshot) {
      if (state.isCurrent && state.handle) {
        state.handle.abort()
      }
    }
    // 若仍有残留（理论上不会发生——abort 走的是同一条 detach 路径），兜底清空。
    this.currentHandlesByBucket.clear()
  }
}
